/** 운영자 — 전체 학원 관리: 목록 + 신규 등록(디렉토리 시딩) + 원장 연결 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { IoAddCircle, IoArrowBack, IoCreateOutline, IoLink, IoClose, IoTrashOutline } from 'react-icons/io5';
import { collection, getDocs } from 'firebase/firestore';
import academyService from '../../services/academyService';
import { db } from '../../firebase/config';
import {
  PageHead, PageTitle, PrimaryBtn, GhostBtn, Card, Table, Empty, IconBtn,
  Overlay, Modal, ModalHead, ModalBody, ModalFoot,
} from '../Console/consoleUi';
import AcademyForm, {
  emptyForm, formFromAcademy, buildAcademyData, FIELD_OPTS,
} from '../Console/AcademyForm';
import DaumPostcodeModal from '../../components/common/DaumPostcodeModal';

const fieldLabel = (v) => (FIELD_OPTS.find((o) => o.v === v) || {}).l || v || '-';

const OperatorAcademiesPage = () => {
  const [mode, setMode] = useState('list');     // 'list' | 'form'
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [members, setMembers] = useState([]);
  const [linkQ, setLinkQ] = useState('');
  const [postcodeOpen, setPostcodeOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const all = await academyService.getAllAcademies();
    // 데모 복제(가상체험 사본) + 빈 draft 숨김
    const clean = all.filter((a) => {
      if (a.id.startsWith('demo-acad-') || a.demoSessionId) return false;
      if (a.status === 'draft' && !(a.name && a.name.trim())) return false;
      return true;
    });
    clean.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setList(clean);
    // 원장 연결용 실제 회원 목록
    const uSnap = await getDocs(collection(db, 'users'));
    setMembers(uSnap.docs
      .map((d) => ({ uid: d.id, ...d.data() }))
      .filter((u) => !u.uid.startsWith('demo-') && !u.demoSessionId && u.provider !== 'demo' && u.uid !== 'superadmin'));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    const id = await academyService.createBlankAcademy();
    setEditingId(id);
    setForm(emptyForm);
    setMode('form');
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm(formFromAcademy(a));
    setMode('form');
  };

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !editingId) return;
    setUploading(true);
    try {
      const up = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const url = await academyService.uploadAcademyPromoPhoto(editingId, file);
        up.push({ url, label: '' });
      }
      set('photos', [...form.photos, ...up]);
    } catch { alert('사진 업로드 실패'); } finally { setUploading(false); }
  };
  const removePhoto = (i) => set('photos', form.photos.filter((_, idx) => idx !== i));
  const setPhotoLabel = (i, v) => set('photos', form.photos.map((p, idx) => (idx === i ? { ...p, label: v } : p)));
  const addTuition = () => set('tuition', [...form.tuition, { label: '', price: '' }]);
  const setTuition = (i, k, v) => set('tuition', form.tuition.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const delTuition = (i) => set('tuition', form.tuition.filter((_, idx) => idx !== i));
  const openPostcode = () => setPostcodeOpen(true);

  // 운영자 등록은 가볍게 — 이름·분야만 필수
  const canSave = !!(form.name.trim() && form.field);

  const onSave = async () => {
    if (!canSave || saving || !editingId) return;
    setSaving(true);
    try {
      await academyService.publishDirectoryAcademy(editingId, buildAcademyData(form));
      await load();
      setMode('list');
    } catch (e) { console.error(e); alert('저장 실패'); } finally { setSaving(false); }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`'${a.name || '이름없음'}' 학원을 삭제할까요?\n되돌릴 수 없어요.`)) return;
    await academyService.deleteAcademy(a.id);
    await load();
  };

  const doLink = async (uid) => {
    if (!uid || !linkTarget) return;
    if (!window.confirm(`'${linkTarget.name || '학원'}'의 원장으로 연결할까요?`)) return;
    try {
      await academyService.linkOwner(linkTarget.id, uid);
      setLinkTarget(null); setLinkQ('');
      await load();
      alert('원장 연결 완료');
    } catch (e) { console.error(e); alert('연결 실패'); }
  };

  const linkCandidates = members.filter((u) => {
    const t = linkQ.trim().toLowerCase();
    if (!t) return true;
    return `${u.name || ''} ${u.displayName || ''} ${u.kakaoNickname || ''} ${u.uid}`.toLowerCase().includes(t);
  });

  const formProps = {
    form, set, uploading, photoInputId: 'op-photos',
    onAddPhotos, removePhoto, setPhotoLabel,
    addTuition, setTuition, delTuition, openPostcode,
  };

  if (mode === 'form') {
    return (
      <>
        <PageHead>
          <PageTitle>
            <h2>{editingId ? '학원 등록 / 수정' : '학원 등록'}</h2>
            <p>운영자가 직접 등록해요. 필수는 이름·분야만 — 나머지는 아는 만큼.</p>
          </PageTitle>
          <GhostBtn onClick={() => { setMode('list'); load(); }}><IoArrowBack size={16} /> 목록</GhostBtn>
        </PageHead>
        <Card><AcademyForm {...formProps} /></Card>
        <SaveFoot>
          <span>{canSave ? '저장하면 디렉토리에 노출돼요 (status: approved)' : '이름·분야는 필수예요'}</span>
          <PrimaryBtn onClick={onSave} disabled={!canSave || saving}>{saving ? '저장 중…' : '등록 (디렉토리 노출)'}</PrimaryBtn>
        </SaveFoot>
      </>
    );
  }

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>학원 관리</h2>
          <p>전체 {list.length}곳 · 남양주 등 동네 학원을 미리 등록해 디렉토리를 채워요.</p>
        </PageTitle>
        <PrimaryBtn onClick={openNew}><IoAddCircle size={18} /> 새 학원 등록</PrimaryBtn>
      </PageHead>

      <Card $pad="0">
        {loading ? <Empty>불러오는 중…</Empty> : list.length === 0 ? (
          <Empty>등록된 학원이 없어요. ‘새 학원 등록’으로 시작하세요.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>학원명</th><th>분야</th><th>지역</th><th>상태</th><th>원장</th>
                <th style={{ width: 170, textAlign: 'right' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 700, color: '#111827' }}>{a.name || <Muted>이름없음</Muted>}</td>
                  <td>{fieldLabel(a.field)}</td>
                  <td>{a.location ? String(a.location).split(' ').slice(0, 2).join(' ') : <Muted>-</Muted>}</td>
                  <td>
                    <Pill $on={a.status === 'approved'}>{a.status === 'approved' ? '노출중' : a.status || 'draft'}</Pill>
                  </td>
                  <td>
                    {a.ownerId && a.ownerId !== '' && a.ownerId !== 'demo'
                      ? <Pill $owner>연결됨</Pill>
                      : <Muted>미연결</Muted>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <GhostBtn onClick={() => setLinkTarget(a)} style={{ padding: '7px 11px' }}><IoLink size={15} /> 원장 연결</GhostBtn>
                    <IconBtn onClick={() => openEdit(a)} title="수정" style={{ marginLeft: 4 }}><IoCreateOutline size={18} /></IconBtn>
                    <IconBtn $danger onClick={() => onDelete(a)} title="삭제"><IoTrashOutline size={18} /></IconBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {linkTarget && (
        <Overlay onClick={() => { setLinkTarget(null); setLinkQ(''); }}>
          <Modal $w="480px" onClick={(e) => e.stopPropagation()}>
            <ModalHead>원장 연결 — {linkTarget.name || '학원'} <IconBtn onClick={() => { setLinkTarget(null); setLinkQ(''); }}><IoClose size={18} /></IconBtn></ModalHead>
            <ModalBody>
              <Guide>원장이 카카오로 로그인한 계정을 <b>닉네임/이름으로 검색</b>해 연결하세요.</Guide>
              <SearchRow>
                <input autoFocus value={linkQ} onChange={(e) => setLinkQ(e.target.value)} placeholder="닉네임 · 이름 검색" />
              </SearchRow>
              <CandList>
                {linkCandidates.length === 0 ? (
                  <Empty>해당하는 회원이 없어요.</Empty>
                ) : linkCandidates.map((u) => (
                  <CandRow key={u.uid}>
                    <CandInfo>
                      <CandName>{u.kakaoNickname || u.name || u.displayName || '(무명)'}</CandName>
                      <CandMeta>{u.provider || '-'}{u.kakaoNickname ? ` · 표시명 ${u.name}` : ''} · {u.uid}</CandMeta>
                    </CandInfo>
                    <GhostBtn onClick={() => doLink(u.uid)} style={{ padding: '7px 13px' }}>연결</GhostBtn>
                  </CandRow>
                ))}
              </CandList>
            </ModalBody>
            <ModalFoot>
              <GhostBtn onClick={() => { setLinkTarget(null); setLinkQ(''); }}>닫기</GhostBtn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}

      {postcodeOpen && (
        <DaumPostcodeModal onClose={() => setPostcodeOpen(false)} onComplete={(addr) => set('location', addr)} />
      )}
    </>
  );
};

export default OperatorAcademiesPage;

const SaveFoot = styled.div`
  margin-top: 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px;
  background: #fff; border: 1px solid #EAEDF3; border-radius: 14px; padding: 16px 20px;
  span { font-size: 13px; font-weight: 600; color: #6B7280; }
`;
const Muted = styled.span` color: #C4C9D2; `;
const Pill = styled.span`
  display: inline-block; padding: 3px 10px; border-radius: 11px; font-size: 12px; font-weight: 700;
  background: ${({ $on, $owner }) => ($owner ? '#ECFDF5' : $on ? '#EEF2FF' : '#F3F4F6')};
  color: ${({ $on, $owner }) => ($owner ? '#059669' : $on ? '#1D4ED8' : '#9CA3AF')};
`;
const Guide = styled.p` font-size: 13px; line-height: 1.6; color: #6B7280; margin-bottom: 14px; `;
const SearchRow = styled.div`
  margin-bottom: 12px;
  input { width: 100%; padding: 11px 13px; border: 1px solid #E2E5EA; border-radius: 9px; font-size: 14px; color: #111827;
    &:focus { border-color: #4A7CFE; outline: none; } }
`;
const CandList = styled.div` max-height: 320px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; `;
const CandRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 12px; border: 1px solid #EEF1F6; border-radius: 9px;
`;
const CandInfo = styled.div` min-width: 0; `;
const CandName = styled.div` font-size: 14px; font-weight: 700; color: #1F2937; `;
const CandMeta = styled.div` font-size: 12px; color: #9CA3AF; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `;
